import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Guid } from 'guid-typescript';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { takeWhile } from 'rxjs/operators';
import { NotificationModel } from '../models/notification.model';
import { ConfigurationService } from '../shared/services/configuration.service';
import { CampaignListModel } from '../models/campaign-list.model';
import { CampaignReportModel } from '../models/campaign-report.model';
import { TimeUnit } from '../models/time-unit.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly configSvc = inject(ConfigurationService);

  private readonly backUrl;

  constructor() {
    this.backUrl = `${this.configSvc.notificationApiUrl}api/notifications`;
  }

  getNotificationsByUser(
    userEmail: string
  ): Observable<Array<NotificationModel>> {
    return this.http
      .get<Array<NotificationModel>>(
        `${this.backUrl}?userEmail=${userEmail}&appConsumer=1`
      )
      .pipe(take(10)); // 1: 'WebApp'
  }

  getAllNotificationsByUser(
    userEmail: string,
    lastDays: number | null = null,
    all: boolean = false,
    takeNumber: number | null = 10
  ): Observable<Array<NotificationModel>> {
    return this.http
      .get<Array<NotificationModel>>(
        `${this.backUrl}/all?userEmail=${userEmail}&appConsumer=1&lastDays=${lastDays}&all=${all}`
      )
      .pipe(
        takeWhile((value, index) => takeNumber == null || index < takeNumber)
      ); // 1: 'WebApp'
  }

  markAsRead(id: Guid): Observable<any> {
    return this.http.patch(`${this.backUrl}/${id.toString()}/read`, {});
  }

  markAllAsRead(notifications: NotificationModel[]): Observable<any> {
    return this.http.patch(`${this.backUrl}/readall`, notifications);
  }

  markAsUnread(id: Guid): Observable<any> {
    return this.http.patch(`${this.backUrl}/${id.toString()}/unread`, {});
  }

  getAllCampaigns(): Observable<Array<CampaignListModel>> {
    return this.http.get<Array<CampaignListModel>>(`${this.configSvc.notificationApiUrl}api/campaigns`);
  }

  getCampaign(campaignId: string | null): Observable<any> {
    return this.http.get<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}`);
  }

  getCampaignReport(campaignId: string): Observable<CampaignReportModel> {
    return this.http.get<CampaignReportModel>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/report`);
  }

  createCampaign(name: string): Observable<string> {
    return this.http.post<string>(`${this.configSvc.notificationApiUrl}api/campaigns`, { name });
  }

  updateCampaign(campaignId: string, campaign: any): Observable<any> {
    return this.http.patch<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}`, campaign);
  }

  updateCampaignEmailDelivery(campaignId: string, campaign: any): Observable<any> {
    return this.http.patch<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/emailDelivery`, campaign);
  }

  updateCampaignPushDelivery(campaignId: string, campaign: any): Observable<any> {
    return this.http.patch<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/pushDelivery`, campaign);
  }

  getCampaignConditions(campaignId: string | null): Observable<any> {
    if (!campaignId) {
      return of(null);
    }
    return this.http.get<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/conditions`);
  }

  createCampaignConditions(campaignId: string, conditions: any): Observable<any> {
    return this.http.post<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/conditions`, conditions);
  }

  saveCampaignConditions(campaignId: string, conditions: any): Observable<any> {
    return this.http.patch<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/conditions`, conditions);
  }

  getCampaignContent(campaignId: string | null): Observable<any> {
    if (!campaignId) {
      return of(null);
    }
    return this.http.get<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/content`);
  }

  createCampaignContent(campaignId: string, content: any): Observable<any> {
    return this.http.post<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/content`, content);
  }

  saveCampaignContent(campaignId: string, content: any): Observable<any> {
    return this.http.patch<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/content`, content);
  }

  getTimeUnits(): Observable<Array<TimeUnit>> {
    return this.http.get<Array<TimeUnit>>(`${this.configSvc.notificationApiUrl}api/campaigns/time-units`);
  }

  updateStatusCampaign(campaignId: string, statusId: number): Observable<any> {
    return this.http.patch<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/status`, { statusId });
  }

  testCampaign(campaignId: string | null, email: string): Observable<any> {
    return this.http.post<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/test?email=${encodeURIComponent(email)}`, {});
  }

  executeCampaign(campaignId: string | null): Observable<string> {
    return this.http.post<any>(`${this.configSvc.notificationApiUrl}api/campaigns/${campaignId}/execute`, {});
  }
}
